// Shared A-F readiness item definitions derived from a single rating year's raw
// assessmentChecklist node. Used by both the header dropdown (expandable list) and the
// per-group inline grid in the Assessments Manager so the two stay in lockstep.

const anyTrue = (obj = {}) => Object.values(obj).some(Boolean);
// A parent section is complete only when every required child under it is complete, so the
// header check reflects the whole section rather than a single field. This keeps the
// collapsed header honest: green means nothing underneath is still outstanding.
const rollup = (children = []) => children.length > 0 && children.every((child) => child.status);

// Ordered items with expandable children for EOC (administrations), Graduation (4/5/6
// year) and Accelerated Testers (the tester list plus the SAT/ACT substitute scores). Each
// parent's status rolls up from its children via rollup().
export const yearChecklistItems = (yearNode = {}) => {
  const node = yearNode || {};
  const eoc = node.eoc || {};
  const grad = node.gradData || {};

  const eocChildren = [
    { label: 'Summer', status: !!eoc.summer },
    { label: 'Winter', status: !!eoc.winter },
    { label: 'Spring', status: !!eoc.spring },
  ];
  const gradChildren = [
    { label: '4-Year', status: !!grad.fourYear },
    { label: '5-Year', status: !!grad.fiveYear },
    { label: '6-Year', status: !!grad.sixYear },
  ];
  // The tester list must be loaded before the SAT/ACT substitute scores count for anything,
  // so it is the first required child; the section is complete only once the list and both
  // score files are present.
  const acceleratedChildren = [
    { label: 'Tester List', status: !!(node.acceleratedTesters && node.acceleratedTesters.file) },
    { label: 'SAT', status: !!(node.sat && node.sat.file) },
    { label: 'ACT', status: !!(node.act && node.act.file) },
  ];

  return [
    { key: 'staar', label: 'STAAR 3-8', status: anyTrue(node.staar) },
    { key: 'staarAlt', label: 'STAAR Alt', status: !!(node.staarAlt && node.staarAlt.april38) },
    { key: 'eoc', label: 'EOC', status: rollup(eocChildren), children: eocChildren },
    { key: 'eocAlt', label: 'EOC Alt', status: !!(node.eocAlt && node.eocAlt.spring) },
    { key: 'telpas', label: 'TELPAS', status: !!(node.telpas && node.telpas.spring) },
    { key: 'telpasAlt', label: 'TELPAS Alt', status: !!(node.telpasAlt && node.telpasAlt.spring) },
    {
      key: 'telpasComposite',
      label: 'TELPAS 2025 Composite',
      status: !!(node.telpasComposite && node.telpasComposite.file),
    },
    {
      key: 'domain3TargetsPriorYear',
      label: 'Prior-Year D3 Indicators',
      status: !!(node.domain3TargetsPriorYear && node.domain3TargetsPriorYear.file),
    },
    { key: 'ccmr', label: 'CCMR', status: !!(node.ccmr && node.ccmr.file) },
    { key: 'gradData', label: 'Graduation', status: rollup(gradChildren), children: gradChildren },
    {
      key: 'acceleratedTesters',
      label: 'Accelerated Testers',
      status: rollup(acceleratedChildren),
      children: acceleratedChildren,
    },
  ];
};

// 17-cell list, ordered for a column-major fill into 5 rows: col1 STAAR/TELPAS family,
// col2 EOC family + prior-year D3, col3 graduation/CCMR/accelerated, col4 SAT/ACT.
export const yearChecklistGrid = (yearNode = {}) => {
  const node = yearNode || {};
  const eoc = node.eoc || {};
  const grad = node.gradData || {};
  return [
    // column 1
    { label: 'STAAR 3-8', status: anyTrue(node.staar) },
    { label: 'STAAR Alt', status: !!(node.staarAlt && node.staarAlt.april38) },
    { label: 'TELPAS', status: !!(node.telpas && node.telpas.spring) },
    { label: 'TELPAS Alt', status: !!(node.telpasAlt && node.telpasAlt.spring) },
    { label: 'TELPAS 2025 Composite', status: !!(node.telpasComposite && node.telpasComposite.file) },
    // column 2
    { label: 'EOC Summer', status: !!eoc.summer },
    { label: 'EOC Winter', status: !!eoc.winter },
    { label: 'EOC Spring', status: !!eoc.spring },
    { label: 'EOC Alt', status: !!(node.eocAlt && node.eocAlt.spring) },
    {
      label: 'Prior-Year D3 Indicators',
      status: !!(node.domain3TargetsPriorYear && node.domain3TargetsPriorYear.file),
    },
    // column 3 (4 items; 5th row left open via a spacer for a future item)
    { label: 'Grad 4-Year', status: !!grad.fourYear },
    { label: 'Grad 5-Year', status: !!grad.fiveYear },
    { label: 'Grad 6-Year', status: !!grad.sixYear },
    { label: 'CCMR', status: !!(node.ccmr && node.ccmr.file) },
    { spacer: true },
    // column 4
    { label: 'Accelerated Testers', status: !!(node.acceleratedTesters && node.acceleratedTesters.file) },
    { label: 'SAT', status: !!(node.sat && node.sat.file) },
    { label: 'ACT', status: !!(node.act && node.act.file) },
  ];
};

// The checklist is a collection-season tool: spring files start arriving in May and
// ratings release in August, with September kept so newly onboarding districts still see
// their list. Outside May-September the checklist hides entirely. The display year is
// always the current calendar year, never the newest seeded year node - a summer EOC
// upload seeds next year's node (summer administrations roll forward) and must not flip
// the display to a season that hasn't started.
export const checklistDisplayYear = (now = new Date()) => {
  const month = now.getMonth();
  if (month < 4 || month > 8) return null;
  return String(now.getFullYear());
};

export default 'checklistItems.js';
